import { Box } from '@mui/material';
import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { CodeEntry } from '../CodeInput';

import { ChangeTarget, SendError } from './CodeStepActions';
import { CodeStepLead } from './CodeStepLead';
import { entryStateOf, isHalted } from './codeStepStatus';
import type { CodeStepStatus } from './codeStepStatus';
import { rootSx } from './codeStepStyles';
import { HaltedStep } from './HaltedStep';

export interface CodeStepProps {
  readonly channel?: string;
  readonly target: string;
  readonly notes?: readonly string[];
  readonly label?: string;
  readonly labelledBy?: string;
  readonly length: number;
  readonly code: string;
  readonly attemptKey?: number;
  readonly resetKey?: number;
  readonly status: CodeStepStatus;
  readonly resendAt: number | null;
  readonly resending?: boolean;
  readonly sendError?: string | null;
  readonly changeLabel: string;
  readonly notice?: React.ReactNode;
  readonly onCodeChange: (code: string) => void;
  readonly onComplete: (code: string) => void;
  readonly onResend: () => void;
  readonly onChangeTarget: () => void;
}

export const CodeStep: React.FC<CodeStepProps> = ({
  channel,
  target,
  notes = [],
  label,
  labelledBy,
  length,
  code,
  attemptKey,
  resetKey,
  status,
  resendAt,
  resending = false,
  sendError = null,
  changeLabel,
  notice,
  onCodeChange,
  onComplete,
  onResend,
  onChangeTarget,
}) => {
  const { t } = useTranslation();
  const sentLineId = useId();
  const notesId = useId();
  const noteLines = notes.map((text, index) => ({ id: `${notesId}-${index}`, text }));
  const describedBy = [sentLineId, ...noteLines.map((note) => note.id)].join(' ');

  const lead = <CodeStepLead sentLineId={sentLineId} channel={channel} target={target} notes={noteLines} />;

  if (isHalted(status)) {
    return (
      <Box sx={rootSx}>
        {lead}
        <HaltedStep
          status={status}
          resending={resending}
          sendError={sendError}
          onResend={onResend}
          change={
            <ChangeTarget
              label={changeLabel}
              describedBy={sentLineId}
              held={false}
              autoFocus={status.kind === 'blocked'}
              onChangeTarget={onChangeTarget}
            />
          }
        />
        {notice}
      </Box>
    );
  }

  const entry = entryStateOf(status);

  return (
    <Box sx={rootSx}>
      {lead}
      <CodeEntry
        label={label ?? t('coreComponents.codeStep.label', 'Enter the code')}
        labelledBy={labelledBy}
        describedBy={describedBy}
        length={length}
        code={code}
        verifying={entry.verifying}
        verifyingText={entry.verifyingText}
        errorText={entry.errorText}
        detailText={entry.detailText}
        resendAt={resendAt}
        resending={resending}
        onCodeChange={onCodeChange}
        onComplete={onComplete}
        onResend={onResend}
        align="start"
        attemptKey={attemptKey}
        resetKey={resetKey}
        alternative={sendError === null ? undefined : <SendError message={sendError} />}
        besideResend={<ChangeTarget label={changeLabel} describedBy={sentLineId} held={entry.verifying} onChangeTarget={onChangeTarget} />}
      />
      {notice}
    </Box>
  );
};
